import {
  BasicTask,
  EditedTask,
  FullTimer,
  NewTaskTag,
  OrganizedTimersEvent,
  OrganizedTimersEvents,
  OrganizedTimersEventsByMonth,
  SearchTask,
  SingleAuditLogChange,
  TagCategory,
  ToastOptions,
} from "@/types";
import { endOfWeek, format, set, startOfWeek } from "date-fns";
import { jwtDecode } from "jwt-decode";
import { toast } from "sonner";

// #region titleUpdate
export function titleUpdate(title: string | undefined): void {
  if (title) {
    document.title = title + " | SuperCharged";
  } else {
    document.title = "SuperCharged";
  }
}
// #endregion titleUpdate

// #region decodeUserJWTRefresh
export function decodeUserJWTRefresh(token: unknown): object | null {
  if (typeof token !== "string") {
    return null;
  }

  try {
    const data: object = jwtDecode(token);
    console.log(data);
    return data;
  } catch (error) {
    console.error(error);
    return null;
  }
}
// #endregion decodeUserJWTRefresh

// #region getUserTokenID
export function getUserTokenID(token: string): number | null {
  try {
    const data: object = jwtDecode(token);
    if (!data) {
      return null;
    }

    console.log(data);

    if ("id" in data && typeof data["id"] === "number") {
      return data["id"];
    }

    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
}
// #endregion getUserTokenID

// #region colorIsDarkAdvanced
export function colorIsDarkAdvanced(bgColor: string) {
  const color = bgColor.charAt(0) === "#" ? bgColor.substring(1, 7) : bgColor;
  const r = parseInt(color.substring(0, 2), 16); // hexToR
  const g = parseInt(color.substring(2, 4), 16); // hexToG
  const b = parseInt(color.substring(4, 6), 16); // hexToB
  const uicolors = [r / 255, g / 255, b / 255];
  const c = uicolors.map((col) => {
    if (col <= 0.03928) {
      return col / 12.92;
    }
    return Math.pow((col + 0.055) / 1.055, 2.4);
  });
  const L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  return L <= 0.179;
}
// #endregion colorIsDarkAdvanced

// #region validateHexColor
// #region Validate Hex Color
export function validateHexColor(color: string): boolean {
  if (color === undefined || color === null || color === "") return false;
  const regex = /^#([a-fA-F0-9]{6})$/;
  return regex.test(color);
}
// #endregion validateHexColor

// #region getMilliseconds
// Utility function to get the milliseconds from the days, hours, minutes, and seconds
export function getMilliseconds({
  d,
  h,
  m,
  s,
}: {
  d: number;
  h: number;
  m: number;
  s: number;
}): number {
  return (d * 24 * 60 * 60 + h * 60 * 60 + m * 60 + s) * 1000;
}
// #endregion getMilliseconds

// #region formatDueDate
export function formatDueDate(
  date: string | Date | null | undefined,
  includeTime: boolean = false,
  showTriggerSeconds: boolean = false
): string {
  if (!date) {
    return "";
  }

  const parsedDate = new Date(date);
  if (isNaN(parsedDate.getTime())) {
    return "";
  }

  const dateFormat = includeTime
    ? showTriggerSeconds
      ? "yyyy/MM/dd HH:mm:ss"
      : "yyyy/MM/dd HH:mm"
    : "yyyy/MM/dd";
  return format(parsedDate, dateFormat);
}
// #endregion formatDueDate

// #region getNewTaskTagByID
// Finds a tag by ID in the list of all tags
export function getNewTaskTagByID(
  id: number,
  allTags: TagCategory[]
): NewTaskTag | undefined {
  for (const tagCategory of allTags) {
    for (const tag of tagCategory.tags) {
      if (tag.id === id) {
        return {
          category_id: tagCategory.id,
          tag: tag.name,
          color: tag.color,
        };
      }
    }
  }
  return undefined;
}
// #endregion getNewTaskTagByID

// #region Toasts
// Error
export function genericFailureToast(
  message: string,

  position?:
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
    | "top-center"
    | "bottom-center",
  actionLabel?: string,
  callback?: () => void
): void {
  toast.error("Error", {
    description: message,
    duration: 3000,
    position: position ?? undefined,
    ...(callback && {
      action: {
        label: actionLabel ?? "",
        onClick: callback,
      },
    }),
  });
}

// Success
export function genericSuccessToast(
  message: string,
  options?: ToastOptions
): void {
  toast.success("Success", {
    description: message,
    duration: 3000,
    position: options?.position ?? undefined,
    ...(options?.callback && {
      action: {
        label: options?.actionLabel ?? "",
        onClick: options?.callback,
      },
    }),
  });
}

// #endregion Toasts

// #region Grammar

export const capitalize = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// #endregion Grammar

// #region getReadableName
export const getReadableName = (url: string) => {
  const params = new URLSearchParams(url.split("?")[1]);
  const title = params.get("title");
  if (title) {
    return title;
  }
  const firstKey = params.keys().next().value;
  if (!firstKey) {
    return "";
  }
  const firstValue = params.get(firstKey);
  return `${firstKey}: ${firstValue}`;
};

// #endregion getReadableName

// #region calculateTimeDifference
export function calculateTimeDifference(
  start: string,
  end: string | undefined | null
): number {
  if (!end) {
    return -1;
  }
  const startDate = new Date(start);
  const endDate = new Date(end);
  const difference = endDate.getTime() - startDate.getTime();
  return difference;
}

// #region formatElapsedTime
// #endregion calculateTimeDifference
export function formatElapsedTime(time: number): string {
  if (time < 0) {
    return "";
  }
  // format as days? hours:minutes:seconds
  const days = Math.floor(time / (1000 * 60 * 60 * 24));
  const hours = Math.floor((time % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((time % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((time % (1000 * 60)) / 1000);
  return `${days ? days + "d " : ""}${hours < 10 ? "0" + hours : hours}:${
    minutes < 10 ? "0" + minutes : minutes
  }:${seconds < 10 ? "0" + seconds : seconds}`;
}
// #endregion formatElapsedTime

// #region totalElapsedTime
export function totalElapsedTime(events: OrganizedTimersEvent[]): number {
  return events.reduce(
    (acc, event) =>
      acc + calculateTimeDifference(event.event_start, event.event_end),
    0
  );
}
// #endregion totalElapsedTime

// #region organizeTimersByDate
// Timers
export function organizeTimersByDate(
  unsortedTimers: FullTimer[]
): OrganizedTimersEvents {
  // sort timers by date
  // get today's date using date-fns
  const today = set(new Date(), {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  console.time("organizeTimersByDate");
  const thisWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const thisWeekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });

  const todayTimers: OrganizedTimersEvent[] = [];
  const thisWeekTimers: OrganizedTimersEvent[] = [];
  const olderTimers: OrganizedTimersEventsByMonth[] = [];
  const noEventsTimers: OrganizedTimersEvent[] = [];

  for (const timer of unsortedTimers) {
    if (!timer.events.length) {
      noEventsTimers.push({
        timer: {
          id: timer.id,
          name: timer.name,
          task_id: timer.task_id,
          user_id: timer.user_id,
          task_name: timer.task_name,
          tags: timer.tags,
          totalTime: timer.totalTime,
        },
        // TODO: check if this is correct
        position: -1,
        event_start: "",
        event_end: "",
      });
      continue;
    }
    for (const event of timer.events) {
      const eventStart = new Date(event.start);

      if (eventStart >= today) {
        todayTimers.push({
          timer: {
            id: timer.id,
            name: timer.name,
            task_id: timer.task_id,
            user_id: timer.user_id,
            task_name: timer.task_name,
            tags: timer.tags,
            totalTime: timer.totalTime,
          },
          position: event.position,
          event_start: event.start,
          event_end: event.end,
        });
      } else if (eventStart >= thisWeekStart && eventStart <= thisWeekEnd) {
        thisWeekTimers.push({
          timer: {
            id: timer.id,
            name: timer.name,
            task_id: timer.task_id,
            user_id: timer.user_id,
            task_name: timer.task_name,
            totalTime: timer.totalTime,
            tags: timer.tags,
          },
          position: event.position,
          event_start: event.start,
          event_end: event.end,
        });
      } else {
        // month and year
        const month =
          eventStart.toLocaleString("default", { month: "long" }) +
          " " +
          eventStart.getFullYear();
        const existingMonth = olderTimers.find((item) => item.month === month);

        const newTimer = {
          timer: {
            id: timer.id,
            name: timer.name,
            task_id: timer.task_id,
            user_id: timer.user_id,
            task_name: timer.task_name,
            tags: timer.tags,
            totalTime: timer.totalTime,
          },
          position: event.position,
          event_start: event.start,
          event_end: event.end,
        };

        if (existingMonth) {
          existingMonth.timers.push(newTimer);
        } else {
          olderTimers.push({
            month,
            timers: [newTimer],
          });
        }
      }
    }
  }
  const sortedTodayTimers = todayTimers.sort(
    (a, b) =>
      new Date(b.event_start).getTime() - new Date(a.event_start).getTime()
  );
  const sortedThisWeekTimers = thisWeekTimers.sort(
    (a, b) =>
      new Date(b.event_start).getTime() - new Date(a.event_start).getTime()
  );
  // sort olderTimers by latest month and year first
  const sortedOlderTimers = olderTimers
    .sort((a, b) => {
      const dateA = new Date(a.month);
      const dateB = new Date(b.month);
      return dateB.getTime() - dateA.getTime();
    })
    .map((item) => ({
      month: item.month,
      timers: item.timers.sort(
        (a, b) =>
          new Date(b.event_start).getTime() - new Date(a.event_start).getTime()
      ),
    }));
  console.log(sortedTodayTimers);
  console.log(sortedThisWeekTimers);
  console.log(sortedOlderTimers);

  console.timeEnd("organizeTimersByDate");
  return {
    today: sortedTodayTimers,
    thisWeek: sortedThisWeekTimers,
    older: sortedOlderTimers,
    noEvents: noEventsTimers,
  };
}
// #endregion organizeTimersByDate

// #region convertOldBasicTask
export function convertOldBasicTask(
  oldTask: BasicTask | SearchTask,
  userTags: TagCategory[]
): EditedTask {
  const id = oldTask.id;
  const assigned_to = oldTask.assignee_id;
  const title = oldTask.title;
  const due_date = oldTask.due_date
    ? formatDueDate(new Date(oldTask.due_date))
    : null;
  const input = undefined;
  const collaborators =
    "collaborators" in oldTask ? oldTask.collaborators : undefined;
  const tags: NewTaskTag[] = oldTask.tags
    .map((tag) => {
      const newTag = getNewTaskTagByID(tag.id, userTags);
      if (newTag) {
        return newTag;
      } else {
        return undefined;
      }
    })
    .filter((tag): tag is NewTaskTag => tag !== undefined);
  const parent_id = undefined;
  const status: string = oldTask.status;
  return {
    id,
    assigned_to,
    title,
    due_date,
    input,
    collaborators,
    tags,
    parent_id,
    status,
  };
}
// #endregion convertOldBasicTask

// #region getNumArrayFromString
export function getNumArrayFromString(str: string): number[] {
  // Ensures only valid numbers are included by filtering out non-numeric strings before conversion
  return str
    .replace("[", "")
    .replace("]", "")
    .split(",")
    .map((num) => num.trim())
    .filter((num) => num !== "" && !isNaN(Number(num)))
    .map((num) => Number(num));
}
// #endregion getNumArrayFromString

// #region getStringArrayFromString
export function getStringArrayFromString(str: string): string[] {
  // '[2,4,10,3]' => ["2","4","10","3"]
  return str
    .replace("[", "")
    .replace("]", "")
    .split(",")
    .map((string) => string.trim());
}
// #endregion getStringArrayFromString

// #region displayChangeFieldName
export function displayChangeFieldName(
  field: SingleAuditLogChange["field"]
): string {
  switch (field) {
    case "status":
      return "Status";
    case "parent_id":
      return "Parent Task";
    case "title":
      return "Title";
    case "description":
      return "Description";
    case "assigned_to":
      return "Assigned To";
    case "collaborators":
      return "Collaborators";
    case "due_date":
      return "Due Date";
    case "tags":
      return "Tags";
    default:
      return field;
  }
}
// #endregion displayChangeFieldName

// #region getBuildDate
export function getBuildDate(): Date | null {
  // get the build date from meta tag
  // <meta name="build-date" content="2025-01-15T06:20:16.027Z" />
  const el = document.querySelector('meta[name="build-date"]');
  if (!el) return null;
  const date = el.getAttribute("content");
  if (!date) return null;
  return new Date(date);
}
// #endregion getBuildDate

// #region copyToClipboard
export function copyToClipboard(type: string, text: string) {
  //------------Copy to clipboard----------------
  navigator.clipboard.writeText(text);
  //Swal toast message to Say Link has been copied to your clipboard
  toast.info(`${type} copied to clipboard`, {
    duration: 3000,
  });
}
// #endregion copyToClipboard

// #region stripTags
export const stripTags = (html: string) => {
  return html.replace(/<\/?[^>]+(>|$)/g, "");
};
// #endregion stripTags

// #region stripChangesTags
export const stripChangesTags = (html: string) => {
  // look for <p><br></p> and replace with a new line
  html = html.replace(/<p><br><\/p>/gi, "\n");

  // look for <br> or <br /> or <br/> and replace with a new line
  html = html.replace(/<br\s*\/?>/gi, "\n");

  //  look for closed <p> tag and replace with a new line
  html = html.replace(/<\/p>/gi, "\n");

  return stripTags(html);
};
// #endregion stripChangesTags

// #region createThumbnail
export async function createThumbnail(
  file: File,
  size: number
): Promise<string | false> {
  return new Promise((resolve, reject) => {
    // Check if the file type is allowed
    if (!file.type.match(/image\/(png|jpg|jpeg|webp|svg\+xml|gif|tiff)/)) {
      resolve(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const imageUrl = reader.result as string;

      const img = new Image();
      img.src = imageUrl;

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (ctx) {
          let width = img.width;
          let height = img.height;
          const maxSide = Math.max(width, height);

          if (maxSide > size) {
            const scaleFactor = size / maxSide;
            width *= scaleFactor;
            height *= scaleFactor;
          }

          canvas.width = width;
          canvas.height = height;

          ctx.drawImage(img, 0, 0, width, height);
          const resizedImageUrl = canvas.toDataURL("image/webp");
          resolve(resizedImageUrl);
        } else {
          resolve(false);
        }
      };
    };

    reader.onerror = (error) => {
      console.error("Error reading file:", error);
      resolve(false);
    };

    reader.readAsDataURL(file);
  });
}
// #endregion createThumbnail

// #region removeImageFromString
export function removeImageFromString(htmlString: string): string {
  // Remove <img> tags and their content
  const imgRegex = /<img[^>]*src="[^"]*"[^>]*>/g;
  return htmlString.replace(imgRegex, "");
}
// #endregion removeImageFromString

// #region getNewDayDate
export function getNewDayDate(date: string | number | Date): Date {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0); // Set time to midnight
  return newDate;
}
// #endregion getNewDayDate
